/**
 * Boli Live - Category Page
 *
 * eBay-style category browsing with:
 * - Breadcrumb navigation
 * - Left sidebar with nested category tree
 * - Filter chips (Condition, Price, Buying Format, All Filters)
 * - Product grid with sort and view toggle
 */

import { useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  ChevronDown,
  Grid,
  List,
  SlidersHorizontal,
  X,
  ArrowRight,
} from 'lucide-react';
import { getProductService } from '@boli/core';
import { ProductCard, ProductCardSkeleton } from '../components/ProductCard';
import { CATEGORY_DATA } from '../lib/category-data';
import { cn } from '../lib/utils';
import type { Product } from '@boli/types';

// Filter options
const CONDITION_OPTIONS = [
  { id: 'new', label: 'New' },
  { id: 'like_new', label: 'Like New' },
  { id: 'good', label: 'Good' },
  { id: 'fair', label: 'Fair' },
];

const PRICE_RANGES = [
  { id: 'under_1000', label: 'Under PKR 1,000', min: 0, max: 1000 },
  { id: '1000_5000', label: 'PKR 1,000 to PKR 5,000', min: 1000, max: 5000 },
  { id: '5000_10000', label: 'PKR 5,000 to PKR 10,000', min: 5000, max: 10000 },
  { id: '10000_50000', label: 'PKR 10,000 to PKR 50,000', min: 10000, max: 50000 },
  { id: 'over_50000', label: 'Over PKR 50,000', min: 50000, max: undefined },
];

const SORT_OPTIONS = [
  { id: 'best_match', label: 'Best Match' },
  { id: 'ending_soonest', label: 'Time: ending soonest' },
  { id: 'newly_listed', label: 'Time: newly listed' },
  { id: 'price_low', label: 'Price + Shipping: lowest first' },
  { id: 'price_high', label: 'Price + Shipping: highest first' },
];

// Fetch products
async function fetchCategoryProducts(
  category: string,
  subCategory?: string,
  filters?: {
    conditions?: string[];
    priceMin?: number;
    priceMax?: number;
    sort?: string;
  }
): Promise<{ products: Product[]; total: number }> {
  try {
    const productService = getProductService();
    // Map sort options to API-compatible values
    const sortMap: Record<string, 'newest' | 'price_asc' | 'price_desc' | 'bestselling'> = {
      'best_match': 'newest',
      'ending_soonest': 'newest',
      'newly_listed': 'newest',
      'price_low': 'price_asc',
      'price_high': 'price_desc',
    };
    // Map top-level category slugs to AuctionCategory type
    const categoryMap: Record<string, 'electronics' | 'fashion' | 'home' | 'vehicles' | 'collectibles' | 'art' | 'sports' | 'other'> = {
      'electronics': 'electronics',
      'fashion': 'fashion',
      'home': 'home',
      'motors': 'vehicles',
      'collectibles': 'collectibles',
      'sports': 'sports',
    };
    // Use top-level category for the category filter
    const categoryValue = category ? categoryMap[category] : undefined;
    const result = await productService.getProducts({
      status: 'active',
      category: categoryValue,
      subCategory: subCategory, // Pass subcategory separately to filter further
      sort: sortMap[filters?.sort || 'best_match'] || 'newest',
      limit: 40,
    });

    // Client-side filtering (ideally would be server-side)
    let filtered = result.products;

    if (filters?.conditions && filters.conditions.length > 0) {
      filtered = filtered.filter(p => p.condition && filters.conditions!.includes(p.condition));
    }

    if (filters?.priceMin !== undefined) {
      filtered = filtered.filter(p => p.price >= filters.priceMin!);
    }

    if (filters?.priceMax !== undefined) {
      filtered = filtered.filter(p => p.price <= filters.priceMax!);
    }

    return { products: filtered, total: filtered.length };
  } catch (error) {
    console.error('Failed to fetch products:', error);
    return { products: [], total: 0 };
  }
}

export default function CategoryPage() {
  const { category, subCategory } = useParams<{ category: string; subCategory?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // UI State
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([category || '']);

  // Filter state from URL
  const conditionsParam = searchParams.get('conditions');
  const selectedConditions = useMemo(
    () => conditionsParam?.split(',').filter(Boolean) || [],
    [conditionsParam]
  );
  const priceMin = searchParams.get('priceMin') ? Number(searchParams.get('priceMin')) : undefined;
  const priceMax = searchParams.get('priceMax') ? Number(searchParams.get('priceMax')) : undefined;
  const selectedSort = searchParams.get('sort') || 'best_match';

  // Get category data
  const categoryData = category ? CATEGORY_DATA[category] : null;
  const allSubCategories = categoryData
    ? [...categoryData.popularCategories, ...categoryData.moreCategories]
    : [];

  // Fetch products
  const { data, isLoading } = useQuery({
    queryKey: ['category-products', category, subCategory, selectedConditions, priceMin, priceMax, selectedSort],
    queryFn: () => fetchCategoryProducts(category || '', subCategory, {
      conditions: selectedConditions,
      priceMin,
      priceMax,
      sort: selectedSort,
    }),
    enabled: !!category,
  });

  const products = data?.products || [];
  const totalResults = data?.total || 0;

  // Update URL params
  const updateFilters = (updates: Record<string, string | undefined>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams);
  };

  // Toggle condition filter
  const toggleCondition = (conditionId: string) => {
    const newConditions = selectedConditions.includes(conditionId)
      ? selectedConditions.filter(c => c !== conditionId)
      : [...selectedConditions, conditionId];
    updateFilters({ conditions: newConditions.length > 0 ? newConditions.join(',') : undefined });
  };

  // Set price range
  const setPriceRange = (min?: number, max?: number) => {
    updateFilters({
      priceMin: min?.toString(),
      priceMax: max?.toString(),
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedConditions.length > 0) count++;
    if (priceMin !== undefined || priceMax !== undefined) count++;
    return count;
  }, [selectedConditions, priceMin, priceMax]);

  // Toggle category expansion in sidebar
  const toggleCategoryExpansion = (catId: string) => {
    setExpandedCategories(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const selectedSortLabel = SORT_OPTIONS.find(s => s.id === selectedSort)?.label || 'Best Match';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-primary hover:underline">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            {categoryData ? (
              <>
                <Link
                  to={`/category/${category}`}
                  className={cn(
                    subCategory ? 'text-gray-500 hover:text-primary hover:underline' : 'text-gray-900 font-medium'
                  )}
                >
                  {categoryData.name}
                </Link>
                {subCategory && (
                  <>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">
                      {allSubCategories.find(s => s.slug === subCategory)?.name || subCategory}
                    </span>
                  </>
                )}
              </>
            ) : (
              <span className="text-gray-900 font-medium capitalize">{category}</span>
            )}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Left Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            {/* Category Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              {categoryData?.name || category}
            </h1>

            {/* Category Tree */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Shop by category</h3>
              <ul className="space-y-1">
                {categoryData && (
                  <li>
                    <button
                      onClick={() => toggleCategoryExpansion(category || '')}
                      className="w-full flex items-center justify-between py-1.5 text-sm text-gray-700 hover:text-primary"
                    >
                      <span className="font-medium">{categoryData.name}</span>
                      <ChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform',
                          expandedCategories.includes(category || '') && 'rotate-180'
                        )}
                      />
                    </button>

                    {expandedCategories.includes(category || '') && (
                      <ul className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-3">
                        {allSubCategories.map(sub => (
                          <li key={sub.id}>
                            <Link
                              to={`/category/${category}/${sub.slug}`}
                              className={cn(
                                'block py-1 text-sm transition-colors',
                                subCategory === sub.slug
                                  ? 'text-primary font-medium'
                                  : 'text-gray-600 hover:text-primary hover:underline'
                              )}
                            >
                              {sub.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}
              </ul>
            </div>

            {/* Condition Filter */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-3">Condition</h3>
              <ul className="space-y-2">
                {CONDITION_OPTIONS.map(option => (
                  <li key={option.id}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedConditions.includes(option.id)}
                        onChange={() => toggleCondition(option.id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">{option.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Price Filter */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Price</h3>
              <ul className="space-y-2">
                {PRICE_RANGES.map(range => (
                  <li key={range.id}>
                    <button
                      onClick={() => setPriceRange(range.min, range.max)}
                      className={cn(
                        'w-full text-left text-sm py-1 transition-colors',
                        priceMin === range.min && priceMax === range.max
                          ? 'text-primary font-medium'
                          : 'text-gray-600 hover:text-primary hover:underline'
                      )}
                    >
                      {range.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Mobile Category Title */}
            <h1 className="lg:hidden text-xl font-bold text-gray-900 mb-4">
              {categoryData?.name || category}
            </h1>

            {/* Promo Banner */}
            {categoryData?.promo && (
              <Link
                to={categoryData.promo.ctaLink}
                className={cn(
                  'block rounded-xl overflow-hidden mb-6',
                  categoryData.promo.bgColor
                )}
              >
                <div className="p-6 md:p-8 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                      {categoryData.promo.title}
                    </h2>
                    <p className="text-white/90 mb-3">{categoryData.promo.subtitle}</p>
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-900 text-sm font-semibold rounded-full">
                      {categoryData.promo.ctaText}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            {/* Filter Bar */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Results Count */}
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{totalResults.toLocaleString()}</span> results
                </p>

                <div className="flex items-center gap-3">
                  {/* Filter Chips - Desktop */}
                  <div className="hidden md:flex items-center gap-2">
                    {/* Condition Chip */}
                    <div className="relative">
                      <button
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 border rounded-full text-sm transition-colors',
                          selectedConditions.length > 0
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        )}
                      >
                        Condition
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* All Filters Button */}
                    <button
                      onClick={() => setIsFilterOpen(!isFilterOpen)}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        activeFilterCount > 0
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      All Filters
                      {activeFilterCount > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-white text-gray-900 text-xs rounded-full">
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Mobile Filter Button */}
                  <button
                    onClick={() => setIsFilterOpen(true)}
                    className="md:hidden flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-gray-900 text-white text-xs rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>

                  {/* View Toggle */}
                  <div className="hidden md:flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={cn(
                        'p-2 transition-colors',
                        viewMode === 'grid' ? 'bg-gray-100 text-primary' : 'text-gray-400 hover:text-gray-600'
                      )}
                    >
                      <Grid className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={cn(
                        'p-2 transition-colors',
                        viewMode === 'list' ? 'bg-gray-100 text-primary' : 'text-gray-400 hover:text-gray-600'
                      )}
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Sort Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setIsSortOpen(!isSortOpen)}
                      className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:border-gray-400 transition-colors"
                    >
                      <span className="hidden sm:inline text-gray-600">Sort:</span>
                      <span className="font-medium text-gray-900">{selectedSortLabel}</span>
                      <ChevronDown className={cn("w-4 h-4 transition-transform", isSortOpen && "rotate-180")} />
                    </button>

                    {isSortOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsSortOpen(false)} />
                        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                          {SORT_OPTIONS.map(option => (
                            <button
                              key={option.id}
                              onClick={() => {
                                updateFilters({ sort: option.id === 'best_match' ? undefined : option.id });
                                setIsSortOpen(false);
                              }}
                              className={cn(
                                'w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors',
                                selectedSort === option.id && 'bg-primary/10 text-primary font-medium'
                              )}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Active Filters */}
              {(selectedConditions.length > 0 || priceMin !== undefined || priceMax !== undefined) && (
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                  <span className="text-sm text-gray-500">Active filters:</span>

                  {selectedConditions.map(cond => (
                    <button
                      key={cond}
                      onClick={() => toggleCondition(cond)}
                      className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors"
                    >
                      {CONDITION_OPTIONS.find(c => c.id === cond)?.label}
                      <X className="w-3 h-3" />
                    </button>
                  ))}

                  {(priceMin !== undefined || priceMax !== undefined) && (
                    <button
                      onClick={() => setPriceRange(undefined, undefined)}
                      className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors"
                    >
                      {priceMin !== undefined && priceMax !== undefined
                        ? `PKR ${priceMin.toLocaleString()} - ${priceMax.toLocaleString()}`
                        : priceMin !== undefined
                        ? `Over PKR ${priceMin.toLocaleString()}`
                        : `Under PKR ${priceMax?.toLocaleString()}`}
                      <X className="w-3 h-3" />
                    </button>
                  )}

                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-primary hover:underline ml-2"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Products Grid */}
            <AnimatePresence mode="wait">
              {isLoading ? (
                <div
                  className={cn(
                    'grid gap-4',
                    viewMode === 'grid'
                      ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                      : 'grid-cols-1'
                  )}
                >
                  {Array.from({ length: 12 }).map((_: unknown, i: number) => (
                    <ProductCardSkeleton key={i} viewMode={viewMode} />
                  ))}
                </div>
              ) : products.length > 0 ? (
                <motion.div
                  className={cn(
                    'grid gap-4',
                    viewMode === 'grid'
                      ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                      : 'grid-cols-1'
                  )}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {products.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.02 }}
                    >
                      <ProductCard product={product} viewMode={viewMode} />
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <p className="text-gray-500 mb-4">No products found in this category.</p>
                  <button
                    onClick={clearAllFilters}
                    className="text-primary hover:underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </AnimatePresence>

            {/* Load More */}
            {products.length > 0 && (
              <div className="text-center mt-8">
                <button className="px-8 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors">
                  Show more
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {isFilterOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50 lg:hidden"
            onClick={() => setIsFilterOpen(false)}
          />
          <div className="fixed inset-y-0 right-0 w-80 max-w-full bg-white z-50 lg:hidden overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Filters</h2>
              <button onClick={() => setIsFilterOpen(false)}>
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* Condition */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Condition</h3>
                <ul className="space-y-2">
                  {CONDITION_OPTIONS.map(option => (
                    <li key={option.id}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedConditions.includes(option.id)}
                          onChange={() => toggleCondition(option.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700">{option.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Price</h3>
                <ul className="space-y-2">
                  {PRICE_RANGES.map(range => (
                    <li key={range.id}>
                      <button
                        onClick={() => {
                          setPriceRange(range.min, range.max);
                          setIsFilterOpen(false);
                        }}
                        className={cn(
                          'w-full text-left text-sm py-1 transition-colors',
                          priceMin === range.min && priceMax === range.max
                            ? 'text-primary font-medium'
                            : 'text-gray-600 hover:text-primary'
                        )}
                      >
                        {range.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="sticky bottom-0 p-4 bg-white border-t border-gray-200">
              <button
                onClick={() => setIsFilterOpen(false)}
                className="w-full py-3 bg-primary text-white font-semibold rounded-full"
              >
                Show {totalResults} results
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
